// Gestión de TAREAS RECURRENTES (plantillas del módulo cross `tasks`) EN LÍNEA
// dentro del panel de Tareas del Home. Sustituye al TemplatesModal (mismo criterio
// que TaskDetailInline/HistoryInline: nada de modales en este bloque).
// Solo owner/manager pueden crear/editar (nivel manage); el backend lo exige — al staff
// el guardado le devuelve 403 y se muestra el error.
import { useEffect, useState } from 'react';
import { Plus, Trash2, Flag, Pause, Play, Camera } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import {
  listTemplates, createTemplate, updateTemplate, deleteTemplate,
  TaskTemplate, Assignee, Recur, recurLabel, DIAS_CORTOS,
} from './api';

const RECUR_OPTS: { value: Recur; label: string }[] = [
  { value: 'daily', label: 'Diaria' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
];

export default function TemplatesInline({ assignees }: { assignees: Assignee[] }) {
  const toast = useToast();
  const [templates, setTemplates] = useState<TaskTemplate[] | null>(null);
  const [title, setTitle] = useState('');
  const [recur, setRecur] = useState<Recur>('daily');
  const [days, setDays] = useState<number[]>([]);
  const [monthDay, setMonthDay] = useState(1);
  const [high, setHigh] = useState(false);
  const [photo, setPhoto] = useState(false);   // F4: exigir foto de evidencia al completar
  const [who, setWho] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => listTemplates().then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  useEffect(() => { load(); }, []);

  const toggleDay = (d: number) =>
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || saving) return;
    if (recur === 'weekly' && days.length === 0) { toast.error('Selecciona al menos un día de la semana'); return; }
    setSaving(true);
    try {
      await createTemplate({
        title: t,
        recur,
        weekdays: recur === 'weekly' ? [...days].sort().join(',') : null,
        month_day: recur === 'monthly' ? monthDay : null,
        priority: high ? 'high' : 'normal',
        requires_photo: photo,
        assigned_user_id: who ? Number(who) : null,
      });
      setTitle(''); setHigh(false); setPhoto(false); setWho(''); setDays([]);
      await load();
      toast.success('Tarea recurrente creada');
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo crear');
    } finally { setSaving(false); }
  };

  const toggleActive = async (tpl: TaskTemplate) => {
    try {
      await updateTemplate(tpl.template_id, { active: tpl.active ? 0 : 1 });
      load();
    } catch (err: any) { toast.error(err?.message || 'No se pudo actualizar'); }
  };

  const togglePhoto = async (tpl: TaskTemplate) => {
    try {
      await updateTemplate(tpl.template_id, { requires_photo: tpl.requires_photo ? 0 : 1 });
      load();
    } catch (err: any) { toast.error(err?.message || 'No se pudo actualizar'); }
  };

  const remove = async (tpl: TaskTemplate) => {
    if (!window.confirm(`¿Eliminar la recurrente "${tpl.title}"? El historial ya resuelto se conserva.`)) return;
    try {
      await deleteTemplate(tpl.template_id);
      load();
    } catch (err: any) { toast.error(err?.message || 'No se pudo eliminar'); }
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin]">
      {/* Alta */}
      <form onSubmit={add} className="mb-3 space-y-2 rounded-xl border border-line bg-surface-2/50 p-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nueva tarea recurrente…"
          className="w-full rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-sm text-content outline-none focus:border-accent/50" />
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5">
            {RECUR_OPTS.map((o) => (
              <button key={o.value} type="button" onClick={() => setRecur(o.value)}
                className={`rounded-md px-2 py-1 text-xs font-medium transition ${recur === o.value ? 'bg-accent text-on-accent' : 'text-content-3 hover:text-content'}`}>
                {o.label}
              </button>
            ))}
          </div>
          {recur === 'monthly' && (
            <label className="flex items-center gap-1.5 text-xs text-content-2">
              el día
              <input type="number" min={1} max={31} value={monthDay}
                onChange={(e) => setMonthDay(Math.min(31, Math.max(1, Number(e.target.value) || 1)))}
                className="w-14 rounded-lg border border-line bg-surface-2 px-2 py-1 text-xs text-content outline-none focus:border-accent/50" />
            </label>
          )}
          <select value={who} onChange={(e) => setWho(e.target.value)} title="Responsable"
            className="max-w-[140px] rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2 outline-none focus:border-accent/50">
            <option value="">Sin responsable</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button type="button" onClick={() => setHigh((v) => !v)} title="Marcar como prioridad alta"
            aria-pressed={high}
            className={`flex h-7 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition ${high ? 'border-rose-400 bg-rose-500/15 text-rose-600' : 'border-line text-content-3 hover:text-content'}`}>
            <Flag size={12} /> Alta
          </button>
          <button type="button" onClick={() => setPhoto((v) => !v)} title="Para completarla habrá que subir foto de evidencia"
            aria-pressed={photo}
            className={`flex h-7 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition ${photo ? 'border-accent/50 bg-accent/15 text-accent-ink' : 'border-line text-content-3 hover:text-content'}`}>
            <Camera size={12} /> Foto
          </button>
          <button type="submit" disabled={!title.trim() || saving}
            className="ml-auto flex h-7 items-center gap-1 rounded-lg bg-accent px-2.5 text-xs font-semibold text-on-accent transition hover:opacity-90 disabled:opacity-40">
            <Plus size={13} /> Crear
          </button>
        </div>
        {recur === 'weekly' && (
          <div className="flex flex-wrap gap-1">
            {DIAS_CORTOS.map((d, i) => (
              <button key={d} type="button" onClick={() => toggleDay(i)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${days.includes(i) ? 'bg-accent text-on-accent' : 'bg-surface-2 text-content-3 hover:text-content'}`}>
                {d}
              </button>
            ))}
          </div>
        )}
      </form>

      {/* Listado */}
      {templates === null ? (
        <p className="py-4 text-center text-sm text-content-3">Cargando…</p>
      ) : templates.length === 0 ? (
        <p className="py-4 text-center text-sm text-content-3">
          Sin recurrentes aún. Crea la primera arriba — cada día que aplique aparecerá sola en el panel de Tareas.
        </p>
      ) : (
        <ul className="space-y-1">
          {templates.map((tpl) => (
            <li key={tpl.template_id}
              className={`group flex items-center gap-2.5 rounded-lg px-2 py-2 transition hover:bg-surface-2 ${tpl.active ? '' : 'opacity-50'}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-content">
                  {tpl.priority === 'high' && <Flag size={11} className="mr-1 inline text-rose-500" />}
                  {tpl.title}
                </p>
                <p className="truncate text-[11px] text-content-3">
                  {recurLabel(tpl)}{tpl.requires_photo ? ' · 📷 con foto' : ''}{tpl.assignee ? ` · ${tpl.assignee}` : ''}{tpl.active ? '' : ' · pausada'}
                </p>
              </div>
              <button onClick={() => togglePhoto(tpl)} title={tpl.requires_photo ? 'Quitar requisito de foto' : 'Exigir foto de evidencia'}
                className={`shrink-0 transition ${tpl.requires_photo ? 'text-accent-ink' : 'text-content-3 hover:text-content'}`}>
                <Camera size={15} />
              </button>
              <button onClick={() => toggleActive(tpl)} title={tpl.active ? 'Pausar' : 'Reanudar'}
                className="shrink-0 text-content-3 transition hover:text-content">
                {tpl.active ? <Pause size={15} /> : <Play size={15} />}
              </button>
              <button onClick={() => remove(tpl)} title="Eliminar"
                className="shrink-0 text-content-3 transition hover:text-rose-500">
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
